import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Car, ChevronDown, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import type { DisruptionTripsPage, DisruptionTripView } from '@/lib/disruptions/types';
import { TRIP_LEG_LABEL_KEY, TRIP_STATUS_PRESENTATION, type TripStatus } from '@/lib/transport/types';
import { shortTime } from '../tower/tower-model';
import { providerLabel } from '@/lib/transport/providers';

// "Cars & on-demand" — the collapsed strip under the runs board, fed by the NEW
// disruption trips read (GET /v1/disruptions/{urn}/trips, seeded once + WS-merged
// by TripChanged). CAR-kind rides only — shuttle seats live on the runs board.
// URNs never render as identity; a trip shows its PNR groupId when the producer
// stamped one, otherwise just the ride facts (mask-by-default).

const TONE_CLASS: Record<string, string> = {
  success: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  info: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  error: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  muted: 'bg-muted text-muted-foreground',
  neutral: 'bg-muted text-muted-foreground',
};

const LIVE: ReadonlySet<TripStatus> = new Set<TripStatus>(['REQUESTED', 'DISPATCHED', 'IN_TRANSIT']);

function carsOf(page: DisruptionTripsPage | null | undefined): DisruptionTripView[] {
  return (page?.trips ?? []).filter((t) => (t.kind ?? 'CAR') === 'CAR');
}

export function CarsStrip({
  page,
  isLoading,
  defaultOpen = false,
  groundAirportIata,
}: {
  page: DisruptionTripsPage | null | undefined;
  isLoading: boolean;
  /** belowThreshold makes the cars strip the primary surface — open it. */
  defaultOpen?: boolean;
  /** Issue #662: Airport for formatting ground times in the correct timezone. */
  groundAirportIata: string | null;
}) {
  const { t } = useTranslation('transport');
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground" data-testid="cars-strip-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> {t('cars.loading')}
      </div>
    );
  }
  if (page === null) {
    return (
      <p className="rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground" data-testid="cars-strip-unavailable">
        {t('cars.unavailable')}
      </p>
    );
  }
  if (!page) return null;

  const cars = carsOf(page);
  const live = cars.filter((trip) => LIVE.has(trip.status)).length;
  const done = cars.filter((trip) => trip.status === 'COMPLETED').length;
  const failed = cars.filter(
    (trip) => trip.status === 'FAILED' || trip.status === 'FAILED_COMPENSATED',
  ).length;
  const onDemand = cars.filter((trip) => trip.dispatchMode === 'ON_DEMAND').length;

  return (
    <section className="rounded-lg border border-border" data-testid="cars-strip">
      <button
        type="button"
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-left text-xs"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="cars-strip-toggle"
      >
        <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open ? '' : '-rotate-90')} aria-hidden />
        <Car className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <span className="font-medium">{t('cars.title')}</span>
        <span className="text-muted-foreground" data-testid="cars-strip-summary">
          {cars.length === 0
            ? t('cars.summaryEmpty')
            : t('cars.summary', { count: cars.length, live, done })}
          {failed > 0 ? (
            <span className="text-rose-700 dark:text-rose-300">
              {' '}
              · {t('cars.failedCount', { count: failed })}
            </span>
          ) : null}
          {onDemand > 0 ? ` · ${t('cars.onDemandCount', { count: onDemand })}` : ''}
        </span>
        {!page.known.transport ? (
          <Badge variant="warning" size="sm">
            {t('badge.transportNotRead')}
          </Badge>
        ) : null}
      </button>
      {open ? (
        cars.length === 0 ? (
          <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground" data-testid="cars-strip-empty">
            {t('cars.empty')}
          </p>
        ) : (
          <ul className="divide-y divide-border border-t border-border" data-testid="cars-strip-list">
            {cars.map((trip) => {
              const p = TRIP_STATUS_PRESENTATION[trip.status];
              return (
                <li key={trip.urn} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5 text-[11px]">
                  <span
                    className={cn('rounded-full px-1.5 py-px font-semibold', TONE_CLASS[p?.tone ?? 'muted'])}
                    data-status={trip.status}
                  >
                    {p ? t(p.labelKey) : trip.status.toLowerCase()}
                  </span>
                  {trip.groupId ? <span className="font-mono font-semibold">{trip.groupId}</span> : null}
                  <span className="text-muted-foreground">
                    {t(TRIP_LEG_LABEL_KEY[trip.leg])} ·{' '}
                    {providerLabel(trip.providerName)}
                    {trip.dispatchMode === 'ON_DEMAND' ? ` · ${t('passengerRequest')}` : ''}
                    {trip.scheduledAt ? ` · ${shortTime(trip.scheduledAt, groundAirportIata)}` : ''}
                    {trip.driverInfo?.licensePlate ? ` · ${trip.driverInfo.licensePlate}` : ''}
                  </span>
                  {trip.lastError ? (
                    <span className="text-amber-700 dark:text-amber-300">{trip.lastError}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )
      ) : null}
    </section>
  );
}
